<template>
  <div class="catalogs-page">
    <header class="catalogs-heading">
      <div>
        <span class="eyebrow">{{ translate('Системные настройки', 'system_settings') }}</span>
        <h1>{{ translate('Справочники', 'catalogs') }}</h1>
        <p>{{ translate('Настройка справочных данных для оборудования, СИЗ и пользователей', 'catalogs_description') }}</p>
      </div>
      <div class="catalogs-summary">
        <div><span>{{ translate('Разделов', 'catalog_groups_count') }}</span><strong>{{ visibleGroups.length }}</strong></div>
        <div><span>{{ translate('Справочников', 'catalogs_count') }}</span><strong>{{ filteredCatalogs.length }}</strong></div>
      </div>
    </header>

    <section class="catalogs-tools">
      <div class="catalog-search">
        <v-icon>mdi-magnify</v-icon>
        <input v-model.trim="search" :placeholder="translate('Поиск по названию или описанию', 'catalog_search_placeholder')">
        <button v-if="search" type="button" :aria-label="translate('Очистить', 'clear')" @click="search = ''"><v-icon small>mdi-close</v-icon></button>
      </div>
      <div class="group-tabs" role="tablist">
        <button type="button" :class="{'group-tab--active': activeGroup === 'all'}" @click="activeGroup = 'all'">
          {{ translate('Все', 'all') }}
        </button>
        <button
            v-for="group in catalogGroups"
            :key="group"
            type="button"
            :class="{'group-tab--active': activeGroup === group}"
            @click="activeGroup = group"
        >
          {{ translate(group, group) }}
        </button>
      </div>
    </section>

    <div v-if="visibleGroups.length" class="catalog-groups">
      <section v-for="group in visibleGroups" :key="group" class="catalog-group">
        <header class="group-heading">
          <div class="group-icon"><v-icon>{{ groupIcon(group) }}</v-icon></div>
          <div><h2>{{ translate(group, group) }}</h2><span>{{ catalogsForGroup(group).length }} {{ translate('справочников', 'catalogs_count') }}</span></div>
        </header>
        <div class="catalog-grid">
          <router-link
              v-for="tab in catalogsForGroup(group)"
              :key="tab.code"
              :to="getRoute(tab)"
              class="catalog-card"
          >
            <div class="catalog-card-icon"><v-icon>{{ catalogIcon(tab) }}</v-icon></div>
            <div class="catalog-card-copy">
              <h3>{{ catalogTitle(tab) }}</h3>
              <p>{{ catalogDescription(tab) }}</p>
              <span class="catalog-code">{{ tab.code }}</span>
            </div>
            <v-icon class="catalog-arrow">mdi-chevron-right</v-icon>
          </router-link>
        </div>
      </section>
    </div>

    <div v-else class="catalog-empty">
      <v-icon>mdi-book-search-outline</v-icon>
      <h2>{{ translate('Справочники не найдены', 'catalogs_not_found') }}</h2>
      <p>{{ translate('Измените поисковый запрос или выберите другой раздел', 'catalogs_not_found_hint') }}</p>
      <v-btn outlined color="primary" @click="resetFilters">{{ translate('Сбросить фильтры', 'reset_filters') }}</v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CatalogsList',
  data: () => ({search: '', activeGroup: 'all'}),
  computed: {
    catalogs() {
      return this.$store.getters.getAllCatalogs.filter(item => this.$store.getters.isPerimitted(item.code, 'view'));
    },
    catalogGroups() {
      return [...new Set(this.catalogs.map(item => item.groupLocalizationCode).filter(Boolean))];
    },
    filteredCatalogs() {
      const needle = this.search.toLocaleLowerCase();
      return this.catalogs.filter(item => {
        if (this.activeGroup !== 'all' && item.groupLocalizationCode !== this.activeGroup) return false;
        if (!needle) return true;
        return [this.catalogTitle(item), this.catalogDescription(item), item.code]
            .filter(Boolean).some(value => String(value).toLocaleLowerCase().includes(needle));
      });
    },
    visibleGroups() {
      return this.catalogGroups.filter(group => this.filteredCatalogs.some(item => item.groupLocalizationCode === group));
    },
  },
  mounted() { document.querySelector('html').style.overflowY = 'auto'; },
  beforeDestroy() { document.querySelector('html').style.overflowY = 'hidden'; },
  methods: {
    translate(def, code, entity) { return this.$store.getters.getTranslation(def, code, entity); },
    catalogsForGroup(group) { return this.filteredCatalogs.filter(item => item.groupLocalizationCode === group); },
    catalogTitle(tab) { return this.translate(tab.code, 'catalog' + tab.name); },
    catalogDescription(tab) { return this.translate(tab.descriptionLocalizationCode, tab.descriptionLocalizationCode) || this.translate('Настройка справочных значений', 'catalog_default_description'); },
    getRoute(tab) { return '/view/' + (tab ? tab.code : ''); },
    resetFilters() { this.search = ''; this.activeGroup = 'all'; },
    groupIcon(group) {
      const value = String(group).toLowerCase();
      if (value.includes('user') || value.includes('employee')) return 'mdi-account-cog-outline';
      if (value.includes('asset') || value.includes('equip')) return 'mdi-tools';
      if (value.includes('system') || value.includes('sys')) return 'mdi-cog-outline';
      if (value.includes('report')) return 'mdi-file-chart-outline';
      return 'mdi-folder-cog-outline';
    },
    catalogIcon(tab) {
      const value = `${tab.code || ''} ${tab.name || ''}`.toLowerCase();
      if (value.includes('maintenance')) return 'mdi-wrench-clock-outline';
      if (value.includes('user') || value.includes('role') || value.includes('privilege')) return 'mdi-account-key-outline';
      if (value.includes('employee')) return 'mdi-account-hard-hat-outline';
      if (value.includes('asset') || value.includes('model')) return 'mdi-package-variant-closed';
      if (value.includes('distribution') || value.includes('location')) return 'mdi-map-marker-radius-outline';
      if (value.includes('ipm') || value.includes('ppe')) return 'mdi-shield-check-outline';
      if (value.includes('status')) return 'mdi-list-status';
      return 'mdi-book-open-page-variant-outline';
    },
  },
};
</script>

<style scoped>
.catalogs-page{min-height:100%;padding:20px 22px 32px;color:#19374f;background:linear-gradient(180deg,#f2f7fa,#f8fafc 300px)}.catalogs-heading{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;margin-bottom:17px}.catalogs-heading h1{margin:4px 0 3px;font-size:29px}.catalogs-heading p{margin:0;color:#7a8d9e}.eyebrow{color:#07888a;font-size:10px;font-weight:800;letter-spacing:.13em;text-transform:uppercase}.catalogs-summary{display:flex;align-items:center;overflow:hidden;border:1px solid #dce7ee;border-radius:12px;background:#fff}.catalogs-summary div{min-width:112px;padding:11px 17px;border-left:1px solid #e4ebf0}.catalogs-summary div:first-child{border-left:0}.catalogs-summary span,.catalogs-summary strong{display:block}.catalogs-summary span{color:#8293a1;font-size:9px;text-transform:uppercase}.catalogs-summary strong{margin-top:2px;color:#07857f;font-size:20px}.catalogs-tools{position:sticky;z-index:2;top:0;margin-bottom:17px;padding:14px;border:1px solid #dce7ee;border-radius:14px;background:rgba(255,255,255,.96);box-shadow:0 4px 15px rgba(25,61,87,.04);backdrop-filter:blur(8px)}.catalog-search{display:flex;align-items:center;height:44px;padding:0 12px;border:1px solid #d8e3e9;border-radius:9px;background:#f9fbfc}.catalog-search>.v-icon{margin-right:9px;color:#758a9a}.catalog-search input{flex:1;min-width:0;color:#2d4b62;font-size:12px;outline:0}.catalog-search button{display:grid;place-items:center;width:30px;height:30px;border-radius:7px}.catalog-search button:hover{background:#eaf2f5}.group-tabs{display:flex;gap:7px;margin-top:11px;padding-bottom:1px;overflow-x:auto}.group-tabs button{flex:0 0 auto;padding:7px 11px;border:1px solid #dce6ec;border-radius:8px;color:#5e7487;font-size:10px;font-weight:700;background:#fff}.group-tabs button:hover{border-color:#9dc9c6;color:#087e78}.group-tabs .group-tab--active{border-color:#0b8982;color:#087e78;background:#e8f7f5}.catalog-groups{display:flex;flex-direction:column;gap:16px}.catalog-group{padding:18px;border:1px solid #dfe8ee;border-radius:14px;background:#fff;box-shadow:0 4px 15px rgba(25,61,87,.035)}.group-heading{display:flex;align-items:center;gap:11px;margin-bottom:14px}.group-icon{display:grid;place-items:center;width:40px;height:40px;border-radius:10px;color:#078984;background:#e6f7f4}.group-icon .v-icon{color:inherit}.group-heading h2{margin:0;color:#27475f;font-size:17px}.group-heading span{display:block;margin-top:2px;color:#8998a5;font-size:9px}.catalog-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}.catalog-card{display:grid;grid-template-columns:42px minmax(0,1fr) 25px;gap:11px;align-items:center;min-height:94px;padding:14px;border:1px solid #e1e9ee;border-radius:11px;color:inherit;text-decoration:none;background:#fbfcfd;transition:.16s ease}.catalog-card:hover{transform:translateY(-2px);border-color:#a8d2cf;background:#f5fbfa;box-shadow:0 7px 18px rgba(27,83,99,.09)}.catalog-card-icon{display:grid;place-items:center;width:42px;height:42px;border-radius:10px;color:#2382a6;background:#e9f5f9}.catalog-card-icon .v-icon{color:inherit}.catalog-card-copy{min-width:0}.catalog-card h3{margin:0;color:#29485f;font-size:12px;line-height:1.3}.catalog-card p{display:-webkit-box;max-height:28px;margin:5px 0 4px;overflow:hidden;color:#7c8e9d;font-size:9px;line-height:1.45;-webkit-line-clamp:2;-webkit-box-orient:vertical}.catalog-code{display:block;overflow:hidden;color:#9aa7b1;font-size:8px;text-overflow:ellipsis;white-space:nowrap}.catalog-arrow{color:#92a4b2}.catalog-empty{display:grid;place-items:center;min-height:330px;padding:30px;text-align:center;border:1px dashed #cddbe3;border-radius:14px;background:#fff}.catalog-empty>.v-icon{font-size:48px;color:#93a6b4}.catalog-empty h2{margin:10px 0 3px;font-size:18px}.catalog-empty p{margin:0 0 16px;color:#8294a3}
@media(max-width:1150px){.catalog-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media(max-width:700px){.catalogs-page{padding:14px}.catalogs-heading{align-items:stretch;flex-direction:column}.catalogs-summary{align-self:flex-start}.catalog-grid{grid-template-columns:1fr}.catalogs-tools{position:static}}
</style>
